import { html, nothing } from "lit";
import { SUBAGENT_HINTS } from "../control-hints.ts";
import type { ChatItem } from "../types/chat-types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import type { ChatProps } from "../views/chat.ts";
import { renderFanInBar } from "./fan-in-bar.ts";
import { deriveFanIn } from "./fan-in-state.ts";
import { shortSessionKey } from "./session-tree.ts";
import {
  isSubagentRunCancellable as isRunCancellable,
  isSubagentRunTerminal as isRunTerminal,
  resolveSubagentActivityPhase,
} from "./subagent-run-status.ts";

/**
 * JAISIU-3319 / Pillar H: per-stage counts shown in the orchestration-group
 * header. Maps directly to the parent's "what happened in this fan-out"
 * checklist and is what the operator glues to when several children
 * stall or fail in the same spawn batch.
 */
export type OrchestrationStageCounts = {
  total: number;
  /** "P" in the plan: queued + spawning + running. */
  inProgress: number;
  /** "M" in the plan: settled cleanly with a successful outcome. */
  done: number;
  /** "K" in the plan: failed + defunct (collapsed; UI shows them separately). */
  failed: number;
  /** Cancelled by the operator (treated as a distinct bucket from failed). */
  cancelled: number;
};

export function countOrchestrationStages(cards: ChatSubagentCard[]): OrchestrationStageCounts {
  let inProgress = 0;
  let done = 0;
  let failed = 0;
  let cancelled = 0;
  for (const card of cards) {
    if (card.status === "cancelled") {
      cancelled += 1;
      continue;
    }
    const phase = resolveSubagentActivityPhase(card);
    if (phase === "ongoing") {
      inProgress += 1;
    } else if (phase === "completed") {
      done += 1;
    } else if (phase === "failed" || phase === "defunct") {
      failed += 1;
    }
  }
  return { total: cards.length, inProgress, done, failed, cancelled };
}

/**
 * Format the stage counts in the plan-spec layout:
 *   `{N} tasks · {M} done · {K} failed · {P} in progress`
 * Cancelled cards are appended only when present (zero suppresses them)
 * so the typical "all clean" fleet reads `4 tasks · 4 done`.
 */
export function formatOrchestrationHeader(cards: ChatSubagentCard[]): string {
  const c = countOrchestrationStages(cards);
  const total = `${c.total} task${c.total === 1 ? "" : "s"}`;
  if (c.total === 0) return "0 tasks";
  const parts: string[] = [total];
  if (c.done > 0) {
    parts.push(`${c.done} done`);
  }
  if (c.failed > 0) {
    parts.push(`${c.failed} failed`);
  }
  if (c.cancelled > 0) {
    parts.push(`${c.cancelled} cancelled`);
  }
  if (c.inProgress > 0) {
    parts.push(`${c.inProgress} in progress`);
  }
  // No stages yet (all "idle" / unknown) — keep "0 in progress" hidden.
  return parts.join(" · ");
}

function legacyStatusCounts(cards: ChatSubagentCard[]): string {
  const counts: Record<string, number> = {};
  for (const c of cards) {
    counts[c.status] = (counts[c.status] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([s, n]) => (n === 1 ? s : `${n} ${s}`))
    .join(" · ");
}

export function describeOrchestrationPattern(cards: ChatSubagentCard[]): string {
  if (cards.length <= 1) {
    return "single";
  }
  const running = cards.filter(
    (c) => c.status === "running" || c.status === "queued" || c.status === "spawning",
  ).length;
  const done = cards.filter((c) => c.status === "done" || c.status === "error").length;
  if (running > 1) {
    return "parallel";
  }
  if (done > 0 && running === 1) {
    return "handoff";
  }
  if (done === cards.length && cards.length > 1) {
    return "fan-in";
  }
  return "parallel";
}

function totalActive(cards: ChatSubagentCard[]): number {
  return cards.filter(
    (c) => c.status === "running" || c.status === "queued" || c.status === "spawning",
  ).length;
}

function renderOrchestrationCompare(
  cards: ChatSubagentCard[],
): ReturnType<typeof html> | typeof nothing {
  const settled = cards.filter((c) => c.status === "done" || c.status === "error");
  if (settled.length < 2) {
    return nothing;
  }
  const ranked = [...settled].sort((a, b) => {
    if (a.status === "done" && b.status !== "done") {
      return -1;
    }
    if (b.status === "done" && a.status !== "done") {
      return 1;
    }
    return (b.outputPreview?.length ?? 0) - (a.outputPreview?.length ?? 0);
  });
  return html`
    <div class="chat-orchestration-compare" role="region" aria-label="Orchestration compare">
      <div class="chat-orchestration-compare__title">Compare results</div>
      <ol class="chat-orchestration-compare__list">
        ${ranked.map(
          (card) => html`
            <li class="chat-orchestration-compare__item">
              <span class="chat-orchestration-compare__label">${card.label}</span>
              <span class="chat-orchestration-compare__status muted">${card.status}</span>
              <p class="chat-orchestration-compare__preview">
                ${
                  card.outputPreview?.trim() ||
                  card.streamText?.trim().slice(0, 280) ||
                  card.error?.trim().slice(0, 200) ||
                  "—"
                }
              </p>
            </li>
          `,
        )}
      </ol>
    </div>
  `;
}

export function renderOrchestrationGroup(
  item: Extract<ChatItem, { kind: "orchestration-group" }>,
  props: ChatProps,
): ReturnType<typeof html> {
  const matched = (item.cardIds ?? [])
    .map((id) => props.chatSubagents?.find((c) => c.id === id))
    .filter(Boolean) as ChatSubagentCard[];
  if (matched.length === 0) return html``;

  const task = matched[0].task;
  const pattern = describeOrchestrationPattern(matched);
  // JAISIU-3319 / Pillar H: collapse the whole group as one unit (the previous
  // implementation toggled only the first card which left the rest stuck
  // open). Track expansion per card id but treat the OR of any card-id as
  // the "is the body visible" gate; the toggle flips all cards together.
  const expandedSet = item.cardIds.filter((id) => props.chatSubagentExpanded?.[id]);
  const isExpanded = expandedSet.length > 0;
  const toggleAll = () => {
    for (const id of item.cardIds) {
      props.onToggleSubagentExpand?.(id);
    }
  };
  const headerText = formatOrchestrationHeader(matched);
  const countsText = legacyStatusCounts(matched);
  // Aggregate fan-in state for this fan-out. Rendered above the collapsible
  // body so "how far along is the whole thing" and "is synthesis blocked?"
  // are answerable without expanding anything or asking the parent chat.
  const fanIn = deriveFanIn(matched, props.fanInSynthesis);

  return html`
    <div
      class="chat-orchestration-group"
      role="region"
      aria-label="Orchestration ${pattern}: ${matched.length} agents"
      data-orchestration-id=${item.orchestrationId ?? ""}
    >
      ${renderFanInBar({
        snapshot: fanIn,
        cards: matched,
        parentSessionKey: props.sessionKey,
        showParent: false,
        onSynthesize:
          props.onSynthesizeFanIn && fanIn.canSynthesize
            ? () => props.onSynthesizeFanIn?.({ force: false })
            : undefined,
        onForceSynthesize:
          props.onSynthesizeFanIn && fanIn.canForceSynthesize
            ? () => props.onSynthesizeFanIn?.({ force: true })
            : undefined,
        onCancelAll: props.onCancelAllSubagents
          ? () =>
              props.onCancelAllSubagents?.({
                parentSessionKey: props.sessionKey,
                childSessionKeys: matched
                  .map((c) => c.childSessionKey)
                  .filter((k): k is string => Boolean(k?.trim())),
              })
          : undefined,
        synthesizing: props.fanInPending?.synthesizing,
        cancelling: props.fanInPending?.cancelling,
        error: props.fanInError,
      })}
      <button
        type="button"
        class="chat-orchestration-group__header"
        data-testid="chat-orchestration-group__header"
        @click=${toggleAll}
        aria-expanded=${isExpanded}
      >
        <span class="chat-orchestration-group__chevron" aria-hidden="true">
          ${isExpanded ? "▼" : "▶"}
        </span>
        <span class="chat-orchestration-group__label" data-testid="chat-orchestration-group__label">
          ${headerText}
        </span>
        <span
          class="chat-orchestration-group__counts muted"
          data-testid="chat-orchestration-group__counts"
        >
          ${countsText}
        </span>
      </button>
      ${task ? html`<div class="chat-orchestration-group__task muted">${task}</div>` : nothing}
      ${
        isExpanded
          ? html`
              <div class="chat-orchestration-group__cards">
                ${matched.map(
                  (card) => html`
                    <div
                      class="chat-orchestration-group__card"
                      data-testid="chat-orchestration-group__card"
                      data-child-session-key=${card.childSessionKey ?? ""}
                    >
                      <span class="chat-orchestration-group__card-label">${card.label}</span>
                      <span
                        class="chat-orchestration-group__card-status chat-subagent-row__status--${card.status}"
                      >
                        ${card.status}
                      </span>
                      ${
                        card.parentSessionKey
                          ? html`<span
                              class="chat-orchestration-group__card-parent mono muted"
                              data-testid="chat-orchestration-group__card-parent"
                              title=${`Spawned by ${card.parentSessionKey}`}
                              >↳ ${shortSessionKey(card.parentSessionKey)}</span
                            >`
                          : nothing
                      }
                      ${
                        card.error
                          ? html`<span class="chat-orchestration-group__card-error muted"
                              >${card.error.slice(0, 100)}</span
                            >`
                          : nothing
                      }
                      ${
                        card.childSessionKey && props.onOpenSubagentSession
                          ? html`
                              <button
                                type="button"
                                class="btn chat-orchestration-group__open-tab"
                                title=${SUBAGENT_HINTS.openSessionTab(card.label)}
                                @click=${(e: Event) => {
                                  e.stopPropagation();
                                  props.onOpenSubagentSession?.({
                                    childKey: card.childSessionKey,
                                    parentKey: props.sessionKey,
                                    label: card.label,
                                  });
                                }}
                              >
                                Chat as ${card.label}
                              </button>
                            `
                          : nothing
                      }
                      <div
                        class="chat-orchestration-group__card-controls"
                        role="group"
                        aria-label="Card controls"
                      >
                        ${
                          card.childSessionKey && props.onCancelSubagentRun
                            ? html`
                                <button
                                  type="button"
                                  class="btn chat-orchestration-group__cancel"
                                  data-testid="chat-orchestration-group__cancel"
                                  ?disabled=${
                                    !isSubagentRunCancellable(card) ||
                                    props.chatSubagentPendingActions?.[card.id] === "cancel"
                                  }
                                  @click=${(e: Event) => {
                                    e.stopPropagation();
                                    if (isSubagentRunCancellable(card)) {
                                      props.onCancelSubagentRun?.(card);
                                    }
                                  }}
                                >
                                  ${
                                    props.chatSubagentPendingActions?.[card.id] === "cancel"
                                      ? "Cancelling…"
                                      : "Cancel"
                                  }
                                </button>
                              `
                            : nothing
                        }
                        ${
                          props.onRetrySubagentRun
                            ? html`
                                <button
                                  type="button"
                                  class="btn chat-orchestration-group__retry"
                                  data-testid="chat-orchestration-group__retry"
                                  ?disabled=${
                                    !isSubagentRunTerminal(card) ||
                                    props.chatSubagentPendingActions?.[card.id] === "retry"
                                  }
                                  @click=${(e: Event) => {
                                    e.stopPropagation();
                                    if (isSubagentRunTerminal(card)) {
                                      props.onRetrySubagentRun?.(card);
                                    }
                                  }}
                                >
                                  ${
                                    props.chatSubagentPendingActions?.[card.id] === "retry"
                                      ? "Retrying…"
                                      : "Retry"
                                  }
                                </button>
                              `
                            : nothing
                        }
                      </div>
                    </div>
                  `,
                )}
              </div>
              ${renderOrchestrationCompare(matched)}
            `
          : nothing
      }
    </div>
  `;
}

function isSubagentRunCancellable(card: ChatSubagentCard): boolean {
  return isRunCancellable(card.status);
}

function isSubagentRunTerminal(card: ChatSubagentCard): boolean {
  return isRunTerminal(card.status);
}
